import { consola } from 'consola'

const logger = consola.withTag('max-webapp')

/** URL официального MAX WebApp Bridge. @see https://dev.max.ru/docs/webapps/bridge */
const BRIDGE_SRC = 'https://st.max.ru/js/max-web-app.js'

/** Контакт пользователя из WebApp.requestContact(). */
export interface MaxContact {
  phone: string
  authDate: string | number
  hash: string
}

/** Распакованные стартовые данные (initDataUnsafe). */
export interface MaxInitDataUnsafe {
  query_id?: string
  auth_date?: number
  hash?: string
  user?: {
    id?: string | number
    first_name?: string
    last_name?: string
    username?: string
    language?: string
    photo_url?: string
  }
  chat?: { id?: string | number; type?: string }
  start_param?: string
}

/**
 * Глобальный объект, который Bridge создаёт автоматически после загрузки скрипта.
 * Описываем только используемое; остальное доступно через index-сигнатуру.
 */
export interface MaxWebAppBridge {
  initData: string
  initDataUnsafe: MaxInitDataUnsafe
  platform: 'ios' | 'android' | 'desktop' | 'web'
  version: string
  requestContact: () => Promise<MaxContact>
  openLink: (url: string) => void
  openMaxLink: (url: string) => void
  enableClosingConfirmation: () => void
  disableClosingConfirmation: () => void
  BackButton: {
    isVisible: boolean
    show: () => void
    hide: () => void
    onClick: (handler: () => void) => void
    offClick: (handler: () => void) => void
  }
  HapticFeedback: {
    impactOccurred: (style: 'soft' | 'light' | 'medium' | 'heavy' | 'rigid') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
    selectionChanged: () => void
  }
  [key: string]: unknown
}

declare global {
  interface Window {
    WebApp?: MaxWebAppBridge
  }
}

let bridgePromise: Promise<MaxWebAppBridge | null> | null = null

/**
 * Быстрый признак запуска внутри MAX — проверяется ДО загрузки Bridge.
 * Платформа добавляет WebAppData в URL-фрагмент при открытии мини-приложения,
 * поэтому скрипт Bridge грузим только при наличии этого сигнала (публичный сайт не трогаем).
 * `?max_webapp=1` — ручной флаг для локальной отладки.
 */
function hasMaxSignal(): boolean {
  if (!process.client) return false
  try {
    if (window.WebApp?.initData) return true
    const hash = window.location.hash || ''
    const search = window.location.search || ''
    return hash.includes('WebAppData') || search.includes('max_webapp=1')
  } catch {
    return false
  }
}

/** Лениво подгружает Bridge-скрипт и резолвит window.WebApp (или null при ошибке). */
function loadBridge(): Promise<MaxWebAppBridge | null> {
  if (!process.client) return Promise.resolve(null)
  if (window.WebApp) return Promise.resolve(window.WebApp)
  if (bridgePromise) return bridgePromise

  bridgePromise = new Promise((resolve) => {
    const finish = () => resolve(window.WebApp ?? null)
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${BRIDGE_SRC}"]`)

    if (existing) {
      if (window.WebApp) return finish()
      existing.addEventListener('load', finish, { once: true })
      existing.addEventListener('error', () => resolve(null), { once: true })
      return
    }

    const el = document.createElement('script')
    el.src = BRIDGE_SRC
    el.async = true
    el.addEventListener('load', finish, { once: true })
    el.addEventListener('error', () => {
      logger.warn('Не удалось загрузить MAX WebApp Bridge')
      resolve(null)
    }, { once: true })
    document.head.append(el)
  })

  return bridgePromise
}

/**
 * Обёртка над MAX WebApp Bridge: детект окружения, ленивая загрузка и безопасные хелперы.
 * Вне MAX все методы — no-op, поэтому композабл безопасен на обычном сайте.
 */
export function useMaxWebApp() {
  const isMaxEnv = (): boolean => hasMaxSignal()

  const ensureBridge = (): Promise<MaxWebAppBridge | null> => loadBridge()

  const getBridge = (): MaxWebAppBridge | null =>
    (process.client && window.WebApp) || null

  const requestContact = async (): Promise<MaxContact | null> => {
    const bridge = await ensureBridge()
    if (!bridge?.requestContact) return null
    try {
      return await bridge.requestContact()
    } catch (error) {
      logger.warn('requestContact отклонён или недоступен:', error)
      return null
    }
  }

  const hapticImpact = (style: Parameters<MaxWebAppBridge['HapticFeedback']['impactOccurred']>[0] = 'light'): void => {
    getBridge()?.HapticFeedback?.impactOccurred(style)
  }

  const hapticNotify = (type: Parameters<MaxWebAppBridge['HapticFeedback']['notificationOccurred']>[0]): void => {
    getBridge()?.HapticFeedback?.notificationOccurred(type)
  }

  const enableClosingConfirmation = (): void => {
    getBridge()?.enableClosingConfirmation?.()
  }

  const disableClosingConfirmation = (): void => {
    getBridge()?.disableClosingConfirmation?.()
  }

  return {
    isMaxEnv,
    ensureBridge,
    getBridge,
    requestContact,
    hapticImpact,
    hapticNotify,
    enableClosingConfirmation,
    disableClosingConfirmation,
  }
}
